import React, { useEffect, useRef } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { strings } from "../../resources/strings";
import { styles } from "../../shared-styles/styles";
import { langAttribute } from "../../utils/questionUtils";
import { useResourcesParticipantWebappFlag } from "../../utils/launchdarkly/featureFlags";
// Imported by direct path, not the components barrel: the barrel pulls in
// Navbar, which imports the routes table, which imports this page.
import LoadingIndicator from "../../components/shared/LoadingIndicator";
import { useAgencyResources } from "../../components/Resources/hooks/useAgencyResources";
import { readDetailState } from "../../components/Resources/resourceListState";
import { ResourceLocationView } from "../../components/Resources/resourceLocations";

const fill = (template: string, value: string): string =>
  template.replace("%1s", value);

const linkClass =
  "inline-flex min-h-11 items-center text-primary underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded";

const NewTabNote: React.FC = () => (
  <span className="sr-only"> {strings.resources.opensInNewTab}</span>
);

const LocationSection: React.FC<{
  location: ResourceLocationView;
  /** Omitted for an unnamed main location: its h2 already says what it is. */
  heading?: string;
  /** Names the Directions link when the location has no name. */
  resourceTitle: string;
}> = ({ location, heading, resourceTitle }) => (
  <section className="flex flex-col gap-1">
    {heading && <h3 className="m-0 text-base font-semibold">{heading}</h3>}
    {location.addressLines.length > 0 && (
      <address className="not-italic text-gray-700">
        {location.addressLines.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </address>
    )}
    {location.phones.length > 0 && (
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {location.phones.map((phone, index) => (
          <li key={`${phone.number}-${index}`}>
            {phone.href ? (
              <a href={phone.href} className={linkClass}>
                {fill(strings.resources.call, phone.number)}
              </a>
            ) : (
              <span>{phone.number}</span>
            )}
            {phone.extension && (
              <span className="text-gray-700">
                {" "}
                {fill(strings.resources.extension, phone.extension)}
              </span>
            )}
            {phone.description && (
              <span className="block text-sm text-gray-700">
                {phone.description}
              </span>
            )}
          </li>
        ))}
      </ul>
    )}
    {location.website && (
      <a
        href={location.website.href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {location.website.label}
        <NewTabNote />
      </a>
    )}
    {location.directionsHref && (
      <a
        href={location.directionsHref}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {fill(strings.resources.directionsTo, location.name || resourceTitle)}
        <NewTabNote />
      </a>
    )}
  </section>
);

export const ResourceDetailContent: React.FC = () => {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { resources, loaded, loading, error } = useAgencyResources();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const resource = resources.find((candidate) => candidate.id === id);
  const fromList = readDetailState(location.state);

  // Opening a resource moves focus to its name, as a page change should.
  // Also once it is clear the resource is not there, so focus is not left on
  // the card that was clicked, which has unmounted.
  const settled = Boolean(resource) || loaded || Boolean(error);
  const resourceId = resource?.id;
  useEffect(() => {
    if (settled) headingRef.current?.focus();
  }, [settled, resourceId]);

  const backToList = (
    <Link
      to={`/resources${fromList?.listSearch ?? ""}`}
      // Back from the list keeps its history entry, search and focus.
      onClick={(event) => {
        if (fromList) {
          event.preventDefault();
          navigate(-1);
        }
      }}
      className={linkClass}
    >
      {strings.resources.allResources}
    </Link>
  );

  let body: React.ReactNode;
  if (resource) {
    const others = resource.otherLocations;
    body = (
      <>
        <div className="flex flex-col gap-2">
          {resource.categoryLabel && (
            <p
              className="m-0 text-sm text-gray-700"
              lang={langAttribute(resource.categoryLocale)}
            >
              {resource.categoryLabel}
            </p>
          )}
          {resource.description && (
            <p className="m-0 whitespace-pre-line text-gray-700">
              {resource.description}
            </p>
          )}
          {resource.keywords.length > 0 && (
            <p className="m-0 text-sm text-gray-700">
              <span className="sr-only">
                {strings.resources.keywordsLabel}{" "}
              </span>
              {resource.keywords.join(", ")}
            </p>
          )}
        </div>
        {resource.mainLocation && (
          <section className="flex flex-col gap-3">
            <h2 className="m-0 text-lg font-semibold">
              {strings.resources.mainLocation}
            </h2>
            <LocationSection
              location={resource.mainLocation}
              heading={resource.mainLocation.name}
              resourceTitle={resource.title}
            />
          </section>
        )}
        {others.length > 0 && (
          <section className="flex flex-col gap-3">
            <h2 className="m-0 text-lg font-semibold">
              {strings.resources.otherLocations}
            </h2>
            {others.map((other, index) => (
              <LocationSection
                // Locations have no id; their order is stable per response.
                key={index}
                location={other}
                heading={
                  other.name ||
                  fill(strings.resources.locationNumber, String(index + 1))
                }
                resourceTitle={resource.title}
              />
            ))}
          </section>
        )}
      </>
    );
  } else if (loading && !loaded) {
    body = (
      <div className="flex justify-center py-10">
        <LoadingIndicator loadingText={strings.resources.loading} />
      </div>
    );
  } else if (error && !loaded) {
    body = (
      <p role="alert" className="text-red-700">
        {strings.resources.loadError}
      </p>
    );
  } else {
    // Deleted since the list loaded, made internal, or not this agency's.
    body = <p className="text-gray-700">{strings.resources.notAvailable}</p>;
  }

  return (
    <main id="main-content" data-theme="repath">
      <div className={styles.pageHeader}>
        <h1 ref={headingRef} tabIndex={-1} className="focus:outline-none">
          {resource?.title || strings.pages.resourceDetail}
        </h1>
      </div>
      <div className="w-full max-w-4xl mx-auto mt-4 flex flex-col gap-6">
        <div>{backToList}</div>
        {body}
      </div>
    </main>
  );
};

/** Flag gate only, like the list: no query runs when the flag is off. */
const ResourceDetail: React.FC = () => {
  const enabled = useResourcesParticipantWebappFlag();
  if (!enabled) {
    return <Navigate to="/" replace />;
  }
  return <ResourceDetailContent />;
};

export default ResourceDetail;
